import { useMemo, useState } from "react";
import { PAYMENT_CONFIG } from "@/constants/status";
import type { FinancialTransaction, TransactionType } from "@/types";
import { formatSAR } from "@/utils/format";
import RelatedOrdersPopover from "@/pages/financial/components/RelatedOrdersPopover";

const TYPE_CONFIG: Record<
  TransactionType,
  { label: string; icon: string; tone: string }
> = {
  sale: { label: "مبيعات", icon: "ri-arrow-down-line", tone: "text-primary-700" },
  settlement: {
    label: "تسوية",
    icon: "ri-bank-line",
    tone: "text-secondary-700",
  },
  refund: {
    label: "استرجاع",
    icon: "ri-refund-2-line",
    tone: "text-accent-700",
  },
  appFee: {
    label: "رسوم التطبيق",
    icon: "ri-smartphone-line",
    tone: "text-accent-700",
  },
  appFeeDebt: {
    label: "رسوم التطبيق المستحقة لطلبات الكاش",
    icon: "ri-error-warning-line",
    tone: "text-red-500",
  },
};

const STATUS_CONFIG: Record<
  FinancialTransaction["status"],
  { label: string; tone: string }
> = {
  completed: { label: "مكتملة", tone: "bg-primary-100 text-primary-800" },
  pending: { label: "معلّقة", tone: "bg-accent-100 text-accent-900" },
  due: { label: "مستحق", tone: "bg-red-100 text-red-600" },
  paid: { label: "مسدد", tone: "bg-primary-100 text-primary-800" },
};

const FILTERS: { key: TransactionType | "all"; label: string }[] = [
  { key: "all", label: "كل العمليات" },
  { key: "sale", label: "المبيعات" },
  { key: "settlement", label: "التسويات" },
  { key: "refund", label: "الاسترجاعات" },
  { key: "appFee", label: "رسوم التطبيق" },
];

interface TransactionsTableProps {
  transactions: FinancialTransaction[];
}

export default function TransactionsTable({
  transactions,
}: TransactionsTableProps) {
  const [filter, setFilter] = useState<TransactionType | "all">("all");

  const visible = useMemo(() => {
    if (filter === "all") return transactions;
    // The app-fee tab groups the fee records with their cash-order due entries.
    if (filter === "appFee") {
      return transactions.filter(
        (item) => item.type === "appFee" || item.type === "appFeeDebt",
      );
    }
    return transactions.filter((item) => item.type === filter);
  }, [transactions, filter]);

  return (
    <section className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card overflow-hidden">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between px-5 py-4 border-b border-background-100">
        <h2 className="text-base font-bold text-foreground-950">
          سجل المعاملات
        </h2>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setFilter(item.key)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                filter === item.key
                  ? "bg-primary-500 text-white"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <p className="md:hidden px-5 pt-4 flex items-center gap-1.5 text-xs text-foreground-500">
        <span className="w-4 h-4 flex items-center justify-center text-primary-500">
          <i className="ri-drag-move-2-line"></i>
        </span>
        اسحب الجدول أفقيًا لعرض بقية الأعمدة
      </p>
      <div className="relative">
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10 md:hidden bg-gradient-to-r from-white via-white/70 to-transparent"></div>
        <div className="overflow-x-auto pb-1">
        <table className="w-full min-w-[820px] text-right border-collapse">
          <thead>
            <tr className="text-xs text-foreground-500 bg-background-50">
              <th className="font-medium px-5 py-3 whitespace-nowrap">المعرّف</th>
              <th className="font-medium px-3 py-3 whitespace-nowrap">التفاصيل</th>
              <th className="font-medium px-3 py-3 whitespace-nowrap">التاريخ</th>
              <th className="font-medium px-3 py-3 whitespace-nowrap">النوع</th>
              <th className="font-medium px-3 py-3 whitespace-nowrap">الوسيلة</th>
              <th className="font-medium px-3 py-3 whitespace-nowrap">المبلغ</th>
              <th className="font-medium px-5 py-3 whitespace-nowrap">الحالة</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((item) => {
              const type = TYPE_CONFIG[item.type];
              const method = PAYMENT_CONFIG[item.method];
              const negative = item.amount < 0;
              return (
                <tr
                  key={item.id}
                  className="border-t border-background-100 hover:bg-primary-50/60 transition-colors duration-200"
                >
                  <td className="px-5 py-3.5 text-sm font-bold text-foreground-950 whitespace-nowrap">
                    {item.orderId ? `#${item.orderId}` : item.id}
                  </td>
                  <td className="px-3 py-3.5 text-sm text-foreground-700">
                    <span>{item.label}</span>
                    {item.branch && (
                      <div className="mt-1 text-xs text-foreground-500">
                        {item.branch}
                      </div>
                    )}
                    {item.reason && (
                      <div className="mt-0.5 text-xs text-foreground-400">
                        {item.reason}
                      </div>
                    )}
                    {item.relatedOrders && item.relatedOrders.length > 0 && (
                      <div className="mt-1.5">
                        <RelatedOrdersPopover orders={item.relatedOrders} />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3.5 text-sm text-foreground-500 whitespace-nowrap">
                    {item.date}
                  </td>
                  <td className="px-3 py-3.5">
                    <span
                      className={`inline-flex items-center gap-1.5 text-sm whitespace-nowrap ${type.tone}`}
                    >
                      <i className={type.icon}></i>
                      {type.label}
                    </span>
                  </td>
                  <td className="px-3 py-3.5">
                    <span className="inline-flex items-center gap-1.5 text-sm text-foreground-600 whitespace-nowrap">
                      <i className={method.icon}></i>
                      {method.label}
                    </span>
                  </td>
                  <td
                    className={`px-3 py-3.5 text-sm font-bold whitespace-nowrap ${
                      negative ? "text-red-500" : "text-primary-700"
                    }`}
                  >
                    {negative ? "-" : "+"}
                    {formatSAR(Math.abs(item.amount))}
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${STATUS_CONFIG[item.status].tone}`}
                    >
                      {STATUS_CONFIG[item.status].label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </section>
  );
}